/**
 * Announcements — zone contractions and Chaos Director events.
 *
 * Both arrive with a `warnMs` because both are announced BEFORE they land.
 * GAME-DESIGN makes the 5-second warning a fairness rule, not a courtesy: a
 * mutator that lands unannounced is a roll a player could not price. So the
 * banner counts the warning down, out loud, and stays on screen for the whole
 * window.
 */

import { playZoneWarnSound, playChaosSound } from '../audio/SoundManager.js';

const FADE_MS = 300;

export class Announcements {
  constructor() {
    this.root = document.getElementById('announce-layer');
    this._live = [];
  }

  /** @param {'zone'|'chaos'} kind */
  push(kind, text, warnMs = 0) {
    const el = document.createElement('div');
    el.className = `announce ${kind}`;
    el.textContent = warnMs > 0 ? `${text} — ${Math.ceil(warnMs / 1000)}s` : text;
    this.root.appendChild(el);

    if (kind === 'zone') playZoneWarnSound();
    else playChaosSound();

    this._live.push({
      el,
      text,
      born: performance.now(),
      // A warned event stays up for its whole window plus a beat; an unwarned
      // one is a statement of fact and only needs long enough to read.
      life: warnMs > 0 ? warnMs + 900 : 2600,
      warnMs,
    });
    while (this._live.length > 3) this._live.shift().el.remove();
  }

  /**
   * One pip of the match-start ritual ("3", "2", "1", "GO!"). Big, centered,
   * and silent here — the beep is the caller's, timed with the pip. Each pip
   * replaces the previous so the count never stacks into a column.
   */
  countdown(text, { gold = false } = {}) {
    for (let i = this._live.length - 1; i >= 0; i--) {
      if (this._live[i].kind !== 'countdown') continue;
      this._live[i].el.remove();
      this._live.splice(i, 1);
    }
    const el = document.createElement('div');
    el.className = 'announce countdown';
    // Inline because styles.css belongs to no workstream; the base .announce
    // class still provides the backdrop and the entry animation.
    el.style.fontSize = '34px';
    el.style.padding = '10px 26px';
    el.style.color = gold ? '#ffd93d' : '#fff';
    el.style.borderColor = gold ? 'rgba(255, 217, 61, 0.8)' : 'rgba(255, 255, 255, 0.55)';
    el.textContent = text;
    this.root.appendChild(el);
    this._live.push({ el, text, kind: 'countdown', born: performance.now(), life: 900, warnMs: 0 });
  }

  update() {
    const now = performance.now();
    for (let i = this._live.length - 1; i >= 0; i--) {
      const a = this._live[i];
      const age = now - a.born;

      if (a.warnMs > 0) {
        const remaining = a.warnMs - age;
        a.el.textContent =
          remaining > 0 ? `${a.text} — ${Math.ceil(remaining / 1000)}s` : `${a.text} — NOW`;
      }

      if (age > a.life + FADE_MS) {
        a.el.remove();
        this._live.splice(i, 1);
      } else if (age > a.life) {
        a.el.classList.add('fading');
      }
    }
  }

  clear() {
    for (const a of this._live) a.el.remove();
    this._live.length = 0;
  }
}

/** Human labels for the zone events the sim emits. */
export function zoneLabel(targetRadius) {
  return targetRadius <= 0 ? 'THE ZONE CLOSES COMPLETELY' : 'THE ZONE IS CONTRACTING';
}
